import { useServices } from '@/app/providers';
import { useCrudNotifications } from '@/shared/hooks';
import type { Routine, RoutineActionCatalogEntry, RoutineCategory, RoutineCronValidationResult } from '@yawi/types';
import { useEffect, useState } from 'react';

interface RoutineFormState {
  name: string;
  description: string;
  category: RoutineCategory | '';
  action: string;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
}

const DEFAULT_FORM: RoutineFormState = {
  name: '',
  description: '',
  category: '',
  action: '',
  cronExpression: '0 9 1 * *',
  timezone: 'America/Mexico_City',
  enabled: true,
};

export function useRoutineForm(routine?: Routine | null, onSuccess?: () => void) {
  const { routineRepo } = useServices();
  const { withCreateNotification, withUpdateNotification } = useCrudNotifications({
    entityName: 'Rutina',
  });
  const [form, setForm] = useState<RoutineFormState>(DEFAULT_FORM);
  const [actions, setActions] = useState<RoutineActionCatalogEntry[]>([]);
  const [cronResult, setCronResult] = useState<RoutineCronValidationResult | null>(null);
  const [validatingCron, setValidatingCron] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingActions, setLoadingActions] = useState(true);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    routineRepo.listActions().then(setActions).finally(() => setLoadingActions(false));
  }, [routineRepo]);

  useEffect(() => {
    if (routine) {
      setForm({
        name: routine.name,
        description: routine.description ?? '',
        category: routine.category,
        action: routine.action,
        cronExpression: routine.cronExpression,
        timezone: routine.timezone,
        enabled: routine.enabled,
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setCronResult(null);
  }, [routine]);

  const setField = <K extends keyof RoutineFormState>(key: K, value: RoutineFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === 'cronExpression') setCronResult(null);
  };

  const validateCron = async () => {
    if (!form.cronExpression.trim()) return;
    setValidatingCron(true);
    try {
      const result = await routineRepo.validateCron(form.cronExpression, form.timezone);
      setCronResult(result);
    } finally {
      setValidatingCron(false);
    }
  };

  const submit = async () => {
    setSaving(true);
    setSubmitError(null);
    try {
      if (routine) {
        const editableFields = routine.isSystem
          ? {
              description: form.description,
              cronExpression: form.cronExpression,
              timezone: form.timezone,
              isActive: form.enabled,
            }
          : {
              name: form.name,
              description: form.description,
              category: form.category as RoutineCategory,
              action: form.action,
              cronExpression: form.cronExpression,
              timezone: form.timezone,
              isActive: form.enabled,
            };
        await withUpdateNotification(() => routineRepo.update(routine.id, editableFields));
      } else {
        await withCreateNotification(() =>
          routineRepo.create({
            name: form.name,
            description: form.description || undefined,
            category: form.category as RoutineCategory,
            action: form.action,
            cronExpression: form.cronExpression,
            timezone: form.timezone,
            isActive: form.enabled,
          }),
        );
      }
      onSuccess?.();
    } catch (error: any) {
      setSubmitError(error?.message ?? 'Error al guardar la rutina');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return { form, setField, actions, loadingActions, cronResult, validatingCron, validateCron, saving, submit, submitError };
}
